import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Banknote,
  Calculator,
  Calendar,
  FileSearch,
  FileText,
  Gem,
  Plus,
  Printer,
  RefreshCw,
  Trash2,
  User,
  Wallet,
  Weight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import StatValue from "@/components/common/StatValue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import useConfirm from "@/hooks/useConfirm";
import useFormatter from "@/hooks/useFormatter";
import type { TransactionItemForm } from "@/schemas/transactionSchema";
import { sumRawWeights, transactionRef } from "@/lib/transactionCalculations";
import { KARAT_DIFF_MARGIN } from "@/consts";
import { services } from "@/services";
import { transactionsKeys } from "@/query-keys";
import useTransactionQuery from "../_hooks/useTransactionQuery";
import useTransactionMutation from "../_hooks/useTransactionMutation";
import EditTransactionForm from "../_components/EditTransactionForm";
import PrintableTransaction, { receiptFileName } from "@/components/PrintableTransaction";

export const Route = createFileRoute("/_private/transactions/$transactionId/")({
  loader: async ({ params, context }) => {
    const transaction = await context.queryClient.ensureQueryData({
      queryKey: transactionsKeys.detail(params.transactionId),
      queryFn: () => services.getTransaction(params.transactionId),
    });
    return { transaction };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const formatter = useFormatter();
  const navigate = useNavigate();
  const { transactionId } = Route.useParams();
  const { transaction: initialTransaction } = Route.useLoaderData();
  const {
    data: transaction,
    isFetching,
    isError,
    error,
    refetch,
  } = useTransactionQuery(transactionId, initialTransaction);
  const { deleteTransaction, isDeleting } = useTransactionMutation();

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    ignoreGlobalStyles: true,
    documentTitle: transaction ? receiptFileName(transaction) : "receipt",
    onBeforePrint: () =>
      new Promise((resolve) => {
        if (!printRef.current) {
          const interval = setInterval(() => {
            if (printRef.current) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
          setTimeout(() => {
            clearInterval(interval);
            resolve();
          }, 2000);
        } else {
          resolve();
        }
      }),
  });

  const deleteConfirm = useConfirm({
    title: t("transactions.confirm.delete.title"),
    description: t("transactions.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleDelete = () =>
    deleteConfirm.confirm(async () => {
      await deleteTransaction(transactionId);
      navigate({ to: "/transactions" });
    });

  if (isError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-destructive">{error?.message}</p>
        <button onClick={() => refetch()} className="text-sm text-primary hover:underline">
          {t("common.retry")}
        </button>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="flex-1 flex flex-col gap-8 pb-8">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-4 w-24" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-48" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border p-3.5 flex flex-col gap-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-24" />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border overflow-hidden">
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const items = (transaction.items as TransactionItemForm[]) ?? [];
  const isInbound = transaction.type === "inbound";
  const totalRawWeight = sumRawWeights(items);

  return (
    <div className="flex-1 flex flex-col gap-8 pb-8">
      {/* Back + Header */}
      <div className="flex flex-col gap-4">
        <Link
          to="/transactions"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("transactions.detail.back")}
        </Link>
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Gem className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight capitalize">
                    {transaction.clientName}
                  </h1>
                  <Badge variant={isInbound ? "success" : "info"}>
                    {t(`transactions.type.${transaction.type}`)}
                  </Badge>
                  {isFetching && (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="font-mono text-xs text-muted-foreground tabular-nums">
                    {transactionRef(transaction.id, transaction.createdAt)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {formatter.dateTime(transaction.createdAt)}
                  </span>
                  {transaction.deletedAt && (
                    <Badge variant="destructive">
                      <Trash2 className="h-3 w-3" />
                      {t("common.deleted")}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePrint()}>
                <Printer className="h-3.5 w-3.5" />
                {t("transactions.detail.print")}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={!!transaction.deletedAt}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t("common.delete")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 h-8">
            <Weight className="h-4 w-4 text-muted-foreground shrink-0" />
            <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
              {t("transactions.detail.weights")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatValue
              icon={<Weight className="h-4 w-4" />}
              label={t("transactions.form.pureWeight")}
              value={formatter.weight(transaction.pureWeight, 3)}
              sub={t("transactions.detail.laborCostHint", {
                rate: formatter.currency(transaction.laborCost),
              })}
            />
            <StatValue
              icon={<Plus className="h-4 w-4" />}
              label={t("transactions.form.pureWeightAdded")}
              value={formatter.weight(transaction.pureWeightAdded, 3)}
              sub={t("transactions.detail.laborCostHint", {
                rate: formatter.currency(transaction.addWeightLaborCost),
              })}
            />
            <StatValue
              icon={<Calculator className="h-4 w-4" />}
              label={t("transactions.form.totalLabor")}
              value={formatter.currency(transaction.totalLabor)}
              sub={
                transaction.pureWeightAdded > 0
                  ? t("transactions.detail.totalLaborAddedHint")
                  : t("transactions.detail.laborCostHint", {
                      rate: formatter.currency(transaction.laborCost),
                    })
              }
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 h-8">
            <Banknote className="h-4 w-4 text-muted-foreground shrink-0" />
            <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
              {t("transactions.detail.financial")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatValue
              icon={<Wallet className="h-4 w-4" />}
              label={t("transactions.form.deposit")}
              value={formatter.currency(transaction.deposit)}
              sub={t("transactions.detail.depositHint")}
            />
            <StatValue
              icon={<User className="h-4 w-4" />}
              label={t("common.createdBy")}
              value={transaction.createdByName}
              sub={t("transactions.detail.createdByHint")}
            />
            <StatValue
              icon={<Calendar className="h-4 w-4" />}
              label={t("common.updatedAt")}
              value={formatter.relativeTime(transaction.updatedAt)}
              sub={t("transactions.detail.updatedAtHint")}
            />
          </div>
        </div>
      </div>

      {transaction.note && (
        <div className="rounded-xl border bg-muted/20 p-4">
          <div className="flex items-center gap-2 h-8 mb-2">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
              {t("common.note")}
            </span>
          </div>
          <p className="text-sm leading-relaxed">{transaction.note}</p>
        </div>
      )}

      {/* Items table */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 h-8">
            <Gem className="h-4 w-4 text-muted-foreground shrink-0" />
            <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
              {t("transactions.items.title")}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isInbound && <EditTransactionForm disabled={items.length === 0} />}
            <span className="text-xs text-muted-foreground tabular-nums">
              {items.length} {t("transactions.detail.items")}
            </span>
          </div>
        </div>

        <div className="rounded-xl border overflow-hidden">
          <ScrollArea className="max-h-110 w-full">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b bg-muted sticky top-0 z-10">
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>{t("transactions.items.name")}</TableHead>
                  <TableHead>{t("transactions.items.declaredKarat")}</TableHead>
                  <TableHead>{t("transactions.items.testedKarat")}</TableHead>
                  <TableHead className="text-right">{t("transactions.items.rawWeight")}</TableHead>
                  <TableHead className="text-right">{t("transactions.items.pureWeight")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <FileSearch className="h-10 w-10 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {t("transactions.detail.itemsEmpty")}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => {
                    const karatDiff =
                      item.testedKarat != null
                        ? Math.abs(item.testedKarat - item.declaredKarat)
                        : 0;
                    const hasKaratMismatch = karatDiff >= KARAT_DIFF_MARGIN;

                    return (
                      <TableRow
                        key={index}
                        className={`hover:bg-muted/10 transition-colors ${item.returned ? "opacity-50" : ""}`}
                      >
                        <TableCell className="text-xs text-muted-foreground">{index + 1}</TableCell>
                        <TableCell className="font-semibold capitalize">
                          <div className="flex items-center gap-1.5">
                            {item.name || "—"}
                            {item.returned && (
                              <Badge variant="warning">{t("transactions.items.returned")}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatter.karat(item.declaredKarat)}
                        </TableCell>
                        <TableCell>
                          {item.testedKarat != null ? (
                            <span className={hasKaratMismatch ? "text-red-600 font-medium" : ""}>
                              {formatter.karat(item.testedKarat)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatter.weight(item.rawWeight, 3)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatter.weight(
                            (item.rawWeight * (item.testedKarat ?? item.declaredKarat)) / 1000,
                            3,
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              <TableFooter>
                {items.length > 0 && (
                  <TableRow className="border-t-2 bg-muted sticky bottom-0 z-10">
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-xs font-mono uppercase text-muted-foreground font-medium">
                      {t("transactions.detail.total")}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatter.weight(totalRawWeight, 3)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatter.weight(transaction.pureWeight, 3)}
                    </TableCell>
                  </TableRow>
                )}
              </TableFooter>
            </Table>
          </ScrollArea>
        </div>
      </section>

      <PrintableTransaction ref={printRef} transaction={transaction} />

      {deleteConfirm.confirmDialog}
    </div>
  );
}
